import { useEffect, useState } from "react";
import {
  BookOpen,
  Bot,
  Briefcase,
  CalendarDays,
  Columns2,
  Compass,
  Gift,
  Headphones,
  Home,
  Landmark,
  Radio,
  Shield,
  Target,
  Timer,
  TrendingUp,
  Wallet,
} from "lucide-react";
import { AnimatePresence, LayoutGroup, motion } from "framer-motion";
import {
  CATEGORIES,
  CONSUMER_BENEFITS,
  ENGINES,
  INSTITUTION_BENEFITS,
} from "../../constants/simmarkets";
import { ArrowIcon, CornerMarks, EASE, SectionShell, useFade, useKnockoutArt } from "./ui";

const CATEGORY_ICONS = {
  shield: Shield,
  landmark: Landmark,
  wallet: Wallet,
  briefcase: Briefcase,
  trend: TrendingUp,
  clock: Timer,
  home: Home,
  radio: Radio,
  compass: Compass,
  book: BookOpen,
};

const ENGINE_ICONS = {
  book: BookOpen,
  bot: Bot,
  columns: Columns2,
  calendar: CalendarDays,
  target: Target,
  headset: Headphones,
  gift: Gift,
};

function MarketProduct() {
  const { fade, reduce } = useFade();
  const [engine, setEngine] = useState(0);

  return (
    <>
      <SectionShell id="offers" labelledBy="offers-heading" tone="mist">
        <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <motion.header
            className="max-w-3xl"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
              What SimMarket offers
            </p>
            <h2
              id="offers-heading"
              className="mt-4 font-semibold leading-[1.08] tracking-tight text-navy [font-size:clamp(2rem,5vw,3.75rem)]"
            >
              One marketplace.
              <span className="mt-1 block text-market-green">Endless financial opportunities.</span>
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-navy/60 sm:text-lg">
              SimMarket supports multiple financial categories. As new financial services emerge,
              additional categories can be seamlessly added to the platform.
            </p>
          </motion.header>

          <ul className="mt-12 grid gap-px overflow-hidden border border-navy/10 bg-navy/10 sm:grid-cols-2 lg:grid-cols-5">
            {CATEGORIES.map((item, index) => {
              const Icon = CATEGORY_ICONS[item.icon] || Compass;
              return (
                <li key={item.name} className="bg-white px-5 py-6">
                  <span className="font-mono text-[11px] tracking-[0.18em] text-market-green">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <Icon className="mt-4 h-5 w-5 text-market-gold" strokeWidth={1.75} aria-hidden="true" />
                  <p className="mt-3 text-sm font-semibold leading-snug tracking-tight text-navy">
                    {item.name}
                  </p>
                </li>
              );
            })}
          </ul>
        </div>
      </SectionShell>

      <SectionShell id="engines" labelledBy="engines-heading" tone="white">
        <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <motion.header
            className="max-w-3xl"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
              Key functionalities
            </p>
            <h2
              id="engines-heading"
              className="mt-4 font-semibold leading-[1.08] tracking-tight text-navy [font-size:clamp(2rem,5vw,3.75rem)]"
            >
              Seven engines behind
              <span className="mt-1 block">every marketplace decision</span>
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-navy/60 sm:text-lg">
              Education, intelligence, comparison, booking, leads, care and loyalty — each with its
              own craft. Step through the engines that turn curiosity into a qualified connection.
            </p>
          </motion.header>

          <EngineBoard engine={engine} setEngine={setEngine} reduce={reduce} fade={fade} />
        </div>
      </SectionShell>

      <SectionShell id="benefits" labelledBy="benefits-heading" tone="mist">
        <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <motion.header
            className="max-w-3xl"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
              Features &amp; benefits
            </p>
            <h2
              id="benefits-heading"
              className="mt-4 font-semibold leading-[1.08] tracking-tight text-navy [font-size:clamp(2rem,5vw,3.75rem)]"
            >
              Value on both sides
              <span className="mt-1 block">of every connection</span>
            </h2>
          </motion.header>

          <div className="mt-12 grid gap-6 lg:grid-cols-2">
            <BenefitGroup index="01" title="For consumers" items={CONSUMER_BENEFITS} />
            <BenefitGroup index="02" title="For financial institutions" items={INSTITUTION_BENEFITS} dark />
          </div>
        </div>
      </SectionShell>
    </>
  );
}

function EngineBoard({ engine, setEngine, reduce, fade }) {
  const active = ENGINES[engine];
  const Icon = ENGINE_ICONS[active.icon] || BookOpen;
  const last = ENGINES.length - 1;
  const go = (index, { focus } = {}) => {
    const next = Math.max(0, Math.min(last, index));
    setEngine(next);
    if (focus) document.getElementById(`engine-tab-${ENGINES[next].id}`)?.focus();
  };

  useEffect(() => {
    [ENGINES[engine + 1], ENGINES[engine - 1]].forEach((item) => {
      if (!item?.art) return;
      const preload = new Image();
      preload.src = item.art;
    });
  }, [engine]);

  const onTrackKeyDown = (event) => {
    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      event.preventDefault();
      go(engine + 1, { focus: true });
    }
    if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      event.preventDefault();
      go(engine - 1, { focus: true });
    }
    if (event.key === "Home") {
      event.preventDefault();
      go(0, { focus: true });
    }
    if (event.key === "End") {
      event.preventDefault();
      go(last, { focus: true });
    }
  };

  return (
    <motion.div
      className="mt-12"
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-80px" }}
      variants={fade}
    >
      <LayoutGroup id="market-engines">
        <div
          role="tablist"
          aria-label="SimMarket engines"
          onKeyDown={onTrackKeyDown}
          className="flex gap-px overflow-x-auto border border-navy/10 bg-navy/10 [-ms-overflow-style:none] [scrollbar-width:none] lg:grid lg:grid-cols-7 lg:overflow-visible [&::-webkit-scrollbar]:hidden"
        >
          {ENGINES.map((item, index) => {
            const selected = index === engine;
            return (
              <button
                key={item.id}
                type="button"
                role="tab"
                id={`engine-tab-${item.id}`}
                aria-selected={selected}
                aria-controls="engine-panel"
                tabIndex={selected ? 0 : -1}
                onClick={() => go(index)}
                className={`relative w-[9.25rem] shrink-0 cursor-pointer px-2.5 pb-3 pt-2 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-market-gold lg:w-auto lg:min-w-0 ${
                  selected ? "bg-market-gold text-navy" : "bg-white text-navy hover:bg-[#f3f7f5]"
                }`}
              >
                {selected ? (
                  <motion.span
                    layoutId="engine-bar"
                    className="absolute inset-x-0 top-0 h-[3px] bg-market-green"
                    transition={reduce ? { duration: 0 } : { type: "spring", stiffness: 380, damping: 34 }}
                  />
                ) : null}
                <span className="relative mt-1 block aspect-[4/3] overflow-hidden bg-[#f3f7f5]">
                  <EngineArt
                    src={item.art}
                    alt=""
                    className="h-full w-full object-contain"
                  />
                </span>
                <span
                  className={`mt-2 block font-mono text-[11px] tracking-[0.16em] ${
                    selected ? "text-navy/50" : "text-market-green"
                  }`}
                >
                  {item.index}
                </span>
                <span className="mt-0.5 block text-sm font-semibold leading-snug tracking-tight">{item.short}</span>
              </button>
            );
          })}
        </div>
      </LayoutGroup>

      <div className="grid overflow-hidden border border-t-0 border-navy/10 bg-navy/10 lg:grid-cols-[minmax(0,1.25fr)_minmax(20rem,0.75fr)]">
        <div
          id="engine-panel"
          role="tabpanel"
          aria-labelledby={`engine-tab-${active.id}`}
          className="relative isolate min-h-[22rem] overflow-hidden bg-[#f3f7f5] lg:min-h-[34rem]"
        >
          <CornerMarks />
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "radial-gradient(55% 50% at 50% 45%, rgba(229,184,11,0.14), transparent 62%), radial-gradient(40% 40% at 88% 12%, rgba(52,132,110,0.12), transparent 55%)",
            }}
            aria-hidden="true"
          />
          <div className="pointer-events-none absolute inset-x-0 top-0 z-[2] h-px bg-gradient-to-r from-transparent via-market-gold to-transparent" />
          <p className="absolute left-5 top-5 z-[3] font-mono text-[11px] tracking-[0.22em] text-market-green sm:left-7 sm:top-7">
            {active.index} / 07
          </p>
          <AnimatePresence mode="wait">
            <motion.div
              key={active.id}
              className="absolute inset-0 z-[1] p-8 sm:p-10 lg:p-12"
              initial={reduce ? false : { opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={reduce ? { opacity: 1 } : { opacity: 0, scale: 1.01 }}
              transition={reduce ? { duration: 0 } : { duration: 0.38, ease: EASE }}
            >
              <EngineArt
                src={active.art}
                alt={active.artAlt}
                className="h-full w-full object-contain"
              />
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="relative flex min-h-[18rem] flex-col bg-white px-6 py-8 sm:px-8 sm:py-10">
          <AnimatePresence mode="wait">
            <motion.div
              key={active.id}
              className="flex flex-1 flex-col"
              initial={reduce ? false : { opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduce ? { opacity: 1 } : { opacity: 0 }}
              transition={reduce ? { duration: 0 } : { duration: 0.28, ease: EASE }}
            >
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-market-green text-white">
                <Icon className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" />
              </span>
              <p className="mt-5 text-[11px] font-semibold uppercase tracking-[0.24em] text-market-green">
                {active.kicker}
              </p>
              <h3 className="mt-3 text-2xl font-semibold tracking-tight text-navy sm:text-[1.85rem]">
                {active.title}
              </h3>
              <p className="mt-3 max-w-xl text-sm leading-relaxed text-navy/65 sm:text-base">{active.copy}</p>
              <ul className="mt-6 flex flex-wrap gap-2">
                {active.items.map((item) => (
                  <li
                    key={item}
                    className="border border-navy/10 bg-[#f3f7f5] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-navy/70"
                  >
                    {item}
                  </li>
                ))}
              </ul>
              {active.result ? (
                <p className="mt-6 border-l-2 border-market-gold pl-4 text-sm font-semibold leading-snug text-navy">
                  {active.result}
                </p>
              ) : null}
            </motion.div>
          </AnimatePresence>

          <div className="mt-8 flex items-center justify-between gap-3 border-t border-navy/10 pt-5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-navy/35">
              {active.short}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => go(engine - 1)}
                disabled={engine === 0}
                className="inline-flex min-h-11 min-w-11 cursor-pointer items-center justify-center border border-navy/15 text-navy transition hover:border-navy disabled:cursor-not-allowed disabled:opacity-35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-market-gold"
                aria-label="Previous engine"
              >
                <span className="rotate-180">
                  <ArrowIcon />
                </span>
              </button>
              <button
                type="button"
                onClick={() => go(engine + 1)}
                disabled={engine === last}
                className="inline-flex min-h-11 min-w-11 cursor-pointer items-center justify-center border border-navy/15 text-navy transition hover:border-navy disabled:cursor-not-allowed disabled:opacity-35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-market-gold"
                aria-label="Next engine"
              >
                <ArrowIcon />
              </button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function EngineArt({ src, alt, className }) {
  const href = useKnockoutArt(src);
  if (!href) {
    return <span className={`block ${className}`} aria-hidden="true" />;
  }
  return <img src={href} alt={alt} decoding="async" className={className} />;
}

function BenefitGroup({ index, title, items, dark = false }) {
  return (
    <div className={`border ${dark ? "border-transparent bg-navy text-white" : "border-navy/10 bg-white text-navy"}`}>
      <div className="border-b border-current/10 px-6 py-5 sm:px-7">
        <p className={`font-mono text-[11px] tracking-[0.2em] ${dark ? "text-market-gold" : "text-market-green"}`}>
          {index}
        </p>
        <h3 className="mt-2 text-xl font-semibold tracking-tight">{title}</h3>
      </div>
      <ul>
        {items.map((item) => (
          <li key={item.title} className="border-t border-current/10 px-6 py-5 sm:px-7">
            <p className="text-base font-semibold tracking-tight">{item.title}</p>
            <p className={`mt-1.5 text-sm leading-relaxed ${dark ? "text-white/65" : "text-navy/60"}`}>
              {item.copy}
            </p>
            {item.items ? (
              <ul className="mt-3 flex flex-wrap gap-1.5">
                {item.items.map((chip) => (
                  <li
                    key={chip}
                    className={`px-2.5 py-1 text-[11px] font-semibold ${
                      dark ? "bg-white/8 text-white/80" : "bg-[#f3f7f5] text-navy/70"
                    }`}
                  >
                    {chip}
                  </li>
                ))}
              </ul>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default MarketProduct;
